import React, { useRef, useState, useEffect, useCallback } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { OnboardingFormData } from '../../types';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card } from "@/components/ui/card";
import { FileText, ArrowLeft, ArrowRight, Edit, Trash2, Check, Euro, Download, RefreshCw, Shield, AlertCircle, Loader2, CheckCircle2 } from "lucide-react";
import { getMainContractSections, getDataProcessingContractSections } from '../../contracts/contractSections';
import { getMainContractText } from '../../contracts/MainContract';
import { getDataProcessingContractText } from '../../contracts/DataProcessingContract';
import ContractViewer from './ContractViewer';
import pdfService from '../../services/pdfService';
import contractService from '../../services/contractService';
import { cn } from "@/lib/utils";

const arrayBufferToBase64 = (buffer: Uint8Array): string => {
  let binary = '';
  const len = buffer.byteLength;
  for (let i = 0; i < len; i += 1) binary += String.fromCharCode(buffer[i]);
  return btoa(binary);
};

/** IBAN-Validierung (ISO 13616): Länge, Zeichen, Prüfsumme (mod 97). */
function validateIBAN(iban: string): { valid: boolean; message?: string } {
  const raw = iban.replace(/\s+/g, '').toUpperCase();
  if (raw.length < 15 || raw.length > 34) return { valid: false, message: 'IBAN hat ungültige Länge (15–34 Zeichen).' };
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/.test(raw)) return { valid: false, message: 'IBAN enthält ungültige Zeichen (nur A–Z und 0–9).' };
  const rearranged = raw.slice(4) + raw.slice(0, 4);
  let numStr = '';
  for (const c of rearranged) {
    numStr += /[A-Z]/.test(c) ? (c.charCodeAt(0) - 55).toString() : c;
  }
  let remainder = 0;
  for (let i = 0; i < numStr.length; i++) {
    remainder = (remainder * 10 + parseInt(numStr[i], 10)) % 97;
  }
  return remainder === 1 ? { valid: true } : { valid: false, message: 'IBAN-Prüfsumme ist ungültig.' };
}

/** BIC/SWIFT-Validierung: 8 oder 11 alphanumerische Zeichen. */
function validateBIC(bic: string): { valid: boolean; message?: string } {
  const raw = bic.replace(/\s+/g, '').toUpperCase();
  if (raw.length !== 8 && raw.length !== 11) return { valid: false, message: 'BIC muss 8 oder 11 Zeichen haben.' };
  if (!/^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/.test(raw)) return { valid: false, message: 'BIC enthält ungültige Zeichen oder Format.' };
  return { valid: true };
}

interface ContractStepProps {
  formData: OnboardingFormData;
  onFormChange: (data: Partial<OnboardingFormData>) => void;
  onNext: () => void;
  onBack: () => void;
  initialAvvConfirmed?: boolean;
}

const ContractStep: React.FC<ContractStepProps> = ({ formData, onFormChange, onNext, onBack, initialAvvConfirmed }) => {
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState<boolean>(formData.hasAcceptedTerms);
  const [mandateReference, setMandateReference] = useState<string>('10001');
  const [mandateFileName, setMandateFileName] = useState<string>('');
  const [generatedMandatePdf, setGeneratedMandatePdf] = useState<Uint8Array | null>(null);
  const [isGeneratingMandate, setIsGeneratingMandate] = useState<boolean>(false);
  const [dataProcessingAcknowledged, setDataProcessingAcknowledged] = useState<boolean>(!!initialAvvConfirmed);
  const directDebitSignatureRef = useRef<SignatureCanvas>(null);
  const directDebitCanvasContainerRef = useRef<HTMLDivElement>(null);

  const currentStep = formData.currentSignatureStep || 0;
  const [mandateExists, setMandateExists] = useState<boolean>(formData.directDebitSignature === 'existing-file');
  const [isInitialLoading] = useState<boolean>(false);
  const [isMandateLoading, setIsMandateLoading] = useState<boolean>(false);

  const contractSteps = [
    { title: "Hauptvertrag", description: "Allgemeine Geschäftsbedingungen", icon: <FileText className="h-5 w-5" /> },
    { title: "Auftragsverarbeitung", description: "Datenschutzvereinbarung (AVV)", icon: <Shield className="h-5 w-5" /> },
    { title: "Lastschriftmandat", description: "SEPA-Lastschrift", icon: <Euro className="h-5 w-5" /> }
  ];

  // ... (useEffect Logik bleibt gleich, ich komprimiere sie für den Upload, aber stelle sicher, dass sie vollständig ist)
  // Ich werde den ganzen useEffect Block übernehmen.

  useEffect(() => {
    // We assume data is pre-loaded by parent (OnboardingForm)
    // Set mandate reference from storage if available
    const savedReference = localStorage.getItem('mandateReference');
    if (savedReference) {
        setMandateReference(savedReference);
    }
    
    // If we have a stored signature (from API), mark mandate as existing
    if (formData.directDebitSignature === 'existing-file') {
        setMandateExists(true);
    }
  }, [formData.directDebitSignature]);

  const createUniqueMandateReference = () => {
    const now = new Date();
    return `${now.getFullYear()}${(now.getMonth()+1).toString().padStart(2,'0')}${now.getDate().toString().padStart(2,'0')}${Math.floor(Math.random()*10000).toString().padStart(4,'0')}`;
  };

  const regenerateMandateReference = useCallback((showMessage = false) => {
    const newReference = createUniqueMandateReference();
    setMandateReference(newReference);
    localStorage.setItem('mandateReference', newReference);
    if (showMessage) setSuccessMessage('Neue Mandatsreferenz erzeugt.');
    return newReference;
  }, []);

  const ensureMandateReference = useCallback(() => {
    if (!mandateReference || mandateReference === '10001') return regenerateMandateReference();
    return mandateReference;
  }, [mandateReference, regenerateMandateReference]);

  useEffect(() => {
    const savedReference = localStorage.getItem('mandateReference');
    if (savedReference) setMandateReference(savedReference);
    else regenerateMandateReference();
  }, [regenerateMandateReference]);

  useEffect(() => {
    if (currentStep === 2) ensureMandateReference();
  }, [currentStep, ensureMandateReference]);

  // Beim Wechsel ins Lastschriftmandat-Tab: Ladescreen anzeigen und vorhandenes Mandat aus der API laden
  useEffect(() => {
    if (currentStep !== 2) return;

    let isMounted = true;
    setIsMandateLoading(true);

    contractService.getLastschriftmandat()
      .then((data) => {
        if (!isMounted) return;
        if (data) {
          const ref = data.mandate_reference;
          if (ref) {
            setMandateReference(ref);
            localStorage.setItem('mandateReference', ref);
          }
          onFormChange({
            payerServiceProviderName: data.payer_name ?? '',
            payerCity: data.payer_city ?? '',
            payerIban: data.payer_iban ?? '',
            payerBic: data.payer_bic ?? '',
            directDebitSignature: 'existing-file',
          });
          setMandateExists(true);
        }
      })
      .catch(() => { /* Fehler still ignorieren, Formular bleibt leer */ })
      .finally(() => {
        if (isMounted) setIsMandateLoading(false);
      });

    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Nur beim Wechsel auf Tab „Lastschriftmandat“ laden
  }, [currentStep]);

  // Resize canvas
  useEffect(() => {
    if (currentStep !== 2) return;
    const resizeCanvas = () => {
      if (directDebitSignatureRef.current && directDebitCanvasContainerRef.current) {
        const canvas = directDebitSignatureRef.current.getCanvas();
        const container = directDebitCanvasContainerRef.current;
        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        canvas.width = container.offsetWidth * ratio;
        canvas.height = 200 * ratio;
        canvas.getContext('2d')?.scale(ratio, ratio);
        
        // Re-render if signature exists
        if (formData.directDebitSignature && formData.directDebitSignature !== 'existing-file') {
           directDebitSignatureRef.current.fromDataURL(formData.directDebitSignature);
        }
      }
    };
    window.addEventListener('resize', resizeCanvas);
    setTimeout(resizeCanvas, 100); // Delay for container render
    return () => window.removeEventListener('resize', resizeCanvas);
  }, [currentStep, formData.directDebitSignature]);

  const handleSepaFieldChange = (field: keyof OnboardingFormData, transform?: (val: string) => string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = transform ? transform(e.target.value) : e.target.value;
    onFormChange({ [field]: value });
  };

  const [isDeletingMandate, setIsDeletingMandate] = useState<boolean>(false);

  const clearDirectDebitSignature = async () => {
    if (mandateExists) {
      setIsDeletingMandate(true);
      setError(null);
      try {
        await contractService.deleteLastschriftmandat();
        setMandateExists(false);
      } catch (err: any) {
        setError(err?.message || 'Fehler beim Löschen des Lastschriftmandats.');
        return;
      } finally {
        setIsDeletingMandate(false);
      }
    }
    directDebitSignatureRef.current?.clear();
    onFormChange({ directDebitSignature: null });
    setGeneratedMandatePdf(null);
    setMandateFileName('');
    setSuccessMessage(null);
  };

  const saveDirectDebitSignature = async () => {
    if (!directDebitSignatureRef.current || directDebitSignatureRef.current.isEmpty()) {
      setError('Bitte unterschreiben Sie das Lastschriftmandat.');
      return;
    }
    if (!formData.payerServiceProviderName || !formData.payerIban || !formData.payerBic || !formData.payerCity) {
      setError('Bitte füllen Sie alle SEPA-Pflichtfelder aus.');
      return;
    }

    const ibanResult = validateIBAN(formData.payerIban);
    if (!ibanResult.valid) {
      setError(ibanResult.message ?? 'Ungültige IBAN.');
      return;
    }
    const bicResult = validateBIC(formData.payerBic);
    if (!bicResult.valid) {
      setError(bicResult.message ?? 'Ungültiger BIC.');
      return;
    }

    setError(null);
    setSuccessMessage(null);
    setIsGeneratingMandate(true);

    try {
      const signatureData = directDebitSignatureRef.current.toDataURL('image/png');
      onFormChange({ directDebitSignature: signatureData });
      
      const effectiveMandateReference = ensureMandateReference();
      
      // PDF Generation logic here (simplified call)
      const pdfBytes = await pdfService.generateSEPAMandat({
        payerName: formData.payerServiceProviderName || formData.companyName,
        payerAddressLines: [formData.street, `${formData.zipCode} ${formData.city}`, formData.country].filter(Boolean),
        iban: formData.payerIban.replace(/\s+/g, '').toUpperCase(),
        bic: formData.payerBic.replace(/\s+/g, '').toUpperCase(),
        mandateReference: effectiveMandateReference,
        signatureImage: signatureData,
        signatureDate: new Date(),
        signatoryName: formData.contactPerson,
        mandateLocation: formData.payerCity
      });

      setGeneratedMandatePdf(pdfBytes);
      const fileName = `SEPA-Lastschriftmandat_${effectiveMandateReference}.pdf`;
      const pdfBase64 = arrayBufferToBase64(pdfBytes);
      
      const requestData = {
        pdf_file: `data:application/pdf;base64,${pdfBase64}`,
        file_name: fileName,
        description: `SEPA-Lastschriftmandat für ${formData.companyName}`,
        mandate_reference: effectiveMandateReference,
        payer_name: formData.payerServiceProviderName,
        payer_iban: formData.payerIban,
        payer_bic: formData.payerBic,
        payer_city: formData.payerCity,
        signature_image: signatureData,
      };

      if (mandateExists) {
        await contractService.updateLastschriftmandat(requestData);
      } else {
        await contractService.uploadLastschriftmandat(requestData);
        setMandateExists(true);
      }
      
      setMandateFileName(fileName);
      setSuccessMessage('Das SEPA-Lastschriftmandat wurde erfolgreich gespeichert.');
    } catch (error: any) {
      setError(`Fehler beim Speichern: ${error.message || 'Unbekannt'}`);
    } finally {
      setIsGeneratingMandate(false);
    }
  };

  const handleDownloadMandate = async () => {
    try {
      if (generatedMandatePdf) {
        await pdfService.downloadPDF(generatedMandatePdf, mandateFileName || 'SEPA-Mandat.pdf');
        return;
      }
      const existing = await contractService.downloadLastschriftmandat();
      if (!existing) { setError('Kein Mandat gefunden.'); return; }
      
      const link = document.createElement('a');
      link.href = existing.pdf_file; // Data URL
      link.download = existing.file_name || 'SEPA-Mandat.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) { setError('Fehler beim Download.'); }
  };

  const handleNextStep = async () => {
    if (currentStep === 0 && !formData.hasAcceptedTerms) {
      setError('Bitte akzeptieren Sie die Vertragsbedingungen.');
      return;
    }
    if (currentStep === 2 && !formData.directDebitSignature) {
      setError('Bitte unterschreiben Sie das Lastschriftmandat.');
      return;
    }
    if (currentStep === 2 && (!formData.payerServiceProviderName || !formData.payerIban || !formData.payerBic || !formData.payerCity)) {
      setError('Bitte füllen Sie alle SEPA-Pflichtfelder aus.');
      return;
    }

    if (currentStep === 1) {
      try {
        await contractService.confirmDataProcessingContract(true);
        setDataProcessingAcknowledged(true);
      } catch (err) {
        setError('AVV konnte nicht bestätigt werden.');
        return;
      }
    }

    if (currentStep < 2) {
      onFormChange({ currentSignatureStep: currentStep + 1 });
      setHasScrolledToBottom(false);
      setError(null);
      setSuccessMessage(null);
    } else {
      onNext();
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 0) {
      onFormChange({ currentSignatureStep: currentStep - 1 });
      setHasScrolledToBottom(false);
      setError(null);
      setSuccessMessage(null);
    } else {
      onBack();
    }
  };

  const handleAcceptTermsChange = (checked: boolean) => {
    if (!hasScrolledToBottom && !formData.hasAcceptedTerms) return;
    
    onFormChange({ hasAcceptedTerms: checked });
    contractService.confirmMainContract(checked).catch(() => {
      setError('Fehler beim Speichern der Bestätigung.');
      onFormChange({ hasAcceptedTerms: !checked });
    });
  };

  if (isInitialLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-strong" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Navigation Tabs */}
      <div className="hidden sm:flex justify-between items-stretch gap-4">
        {contractSteps.map((step, index) => {
          const isActive = currentStep === index;
          const isCompleted = (index === 0 && formData.hasAcceptedTerms) || 
                              (index === 1 && (dataProcessingAcknowledged || currentStep > 1)) || 
                              (index === 2 && !!formData.directDebitSignature);
                              
          return (
            <div 
              key={index}
              onClick={() => onFormChange({ currentSignatureStep: index })}
              className={cn(
                "flex-1 flex flex-col items-center p-4 rounded-xl border-2 transition-all cursor-pointer",
                isActive
                  ? "border-[#1967D2] bg-primary/10"
                  : "border-transparent bg-background hover:bg-muted"
              )}
            >
              <div className={cn(
                "w-10 h-10 rounded-full flex items-center justify-center mb-3 transition-colors",
                isActive ? "bg-[#1967D2] text-white" : "bg-card text-muted-foreground"
              )}>
                {step.icon}
              </div>
              <span className={cn(
                "text-sm font-medium mb-1",
                isActive ? "text-[#1967D2]" : "text-muted-foreground"
              )}>
                {step.title}
              </span>
              <div className={cn(
                "w-2 h-2 rounded-full mt-2 transition-all",
                isCompleted ? "bg-success" : "bg-transparent"
              )} />
            </div>
          );
        })}
      </div>

      {/* Mobile Step Title */}
      <div className="sm:hidden text-center mb-4">
        <span className="text-xs text-muted-foreground block mb-1">Schritt {currentStep + 1} von 3</span>
        <h3 className="text-lg font-semibold text-[#1967D2] flex items-center justify-center gap-2">
          {contractSteps[currentStep].icon}
          {contractSteps[currentStep].title}
        </h3>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Content based on Step */}
      {currentStep === 0 && (
        <div className="space-y-4">
          <ContractViewer
            contractType="main"
            title="Hauptvertrag"
            description="Vertrag über die Nutzung des KI-Terminbot"
            sections={getMainContractSections({
              companyName: formData.companyName,
              street: formData.street,
              zipCode: formData.zipCode || '[PLZ]',
              city: formData.city || '[Ort]',
              country: formData.country,
              contactPerson: formData.contactPerson,
              email: formData.email,
              phone: formData.phone
            })}
            onScrollComplete={() => setHasScrolledToBottom(true)}
            showDownload={true}
            onDownload={async () => {
              try {
                const contractText = getMainContractText(
                  formData.companyName,
                  formData.street,
                  formData.zipCode || '[PLZ]',
                  formData.city || '[Ort]',
                  formData.contactPerson
                );
                const pdfBytes = await pdfService.generateContractPDF({
                  title: 'Hauptvertrag - AI-Assistant fuer TimeGlobe',
                  content: contractText,
                  companyInfo: {
                    name: formData.companyName,
                    address: `${formData.street}, ${formData.zipCode} ${formData.city}`
                  }
                });
                await pdfService.downloadPDF(pdfBytes, `Hauptvertrag_${(formData.companyName || 'Vertrag').replace(/\s+/g, '_')}.pdf`);
              } catch (err) {
                setError('Fehler beim Erstellen des PDFs.');
              }
            }}
          />
          
          <div className="flex items-center space-x-2 p-4 bg-muted rounded-lg border border-border">
            <Checkbox
              id="acceptTerms"
              checked={formData.hasAcceptedTerms}
              onCheckedChange={handleAcceptTermsChange}
              disabled={!hasScrolledToBottom}
            />
            <Label
              htmlFor="acceptTerms"
              className={cn(
                "text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
                !hasScrolledToBottom && "text-muted-foreground/70"
              )}
            >
              {hasScrolledToBottom ? 'Ich akzeptiere die Vertragsbedingungen' : 'Bitte scrollen Sie den Vertrag vollständig durch'}
            </Label>
          </div>
        </div>
      )}

      {currentStep === 1 && (
        <ContractViewer
          contractType="dataProcessing"
          title="Auftragsverarbeitungsvertrag"
          description="Vereinbarung zur Verarbeitung personenbezogener Daten gemäß Art. 28 DSGVO"
          sections={getDataProcessingContractSections({
            companyName: formData.companyName,
            street: formData.street,
            zipCode: formData.zipCode || '[PLZ]',
            city: formData.city || '[Ort]',
            country: formData.country,
            contactPerson: formData.contactPerson,
            email: formData.email,
            phone: formData.phone
          })}
          onScrollComplete={() => setHasScrolledToBottom(true)}
          showDownload={true}
          onDownload={async () => {
            try {
              const contractText = getDataProcessingContractText(
                formData.companyName,
                formData.street,
                formData.zipCode || '[PLZ]',
                formData.city || '[Ort]',
                formData.country,
                formData.contactPerson
              );
              const pdfBytes = await pdfService.generateContractPDF({
                title: 'Auftragsverarbeitungsvertrag (AVV) gemaess Art. 28 DSGVO',
                content: contractText,
                companyInfo: {
                  name: formData.companyName,
                  address: `${formData.street}, ${formData.zipCode} ${formData.city}`
                }
              });
              await pdfService.downloadPDF(pdfBytes, `AVV_${(formData.companyName || 'Vertrag').replace(/\s+/g, '_')}.pdf`);
            } catch (err) {
              setError('Fehler beim Erstellen des PDFs.');
            }
          }}
        />
      )}

      {currentStep === 2 && (
        <>
          {isMandateLoading ? (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <Loader2 className="h-10 w-10 animate-spin text-[#1967D2] mb-4" />
              <p className="text-sm text-muted-foreground">Lastschriftmandat wird geladen …</p>
            </div>
          ) : (
        <Card className="p-6 bg-muted border-border">
          <div className="flex items-start gap-4 mb-6">
            <div className="p-3 bg-card rounded-lg shadow-sm text-muted-foreground">
              <Euro className="h-6 w-6" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">SEPA-Lastschriftmandat</h3>
              <p className="text-sm text-muted-foreground">Ermächtigung zum Lastschrifteinzug</p>
            </div>
          </div>

          <div className="bg-card p-4 rounded-lg border border-border mb-6 text-sm text-muted-foreground space-y-2">
            <p className="font-medium text-foreground">So funktioniert's:</p>
            <ol className="list-decimal list-inside space-y-1 pl-1">
              <li>Überprüfen Sie Ihre Angaben und die Mandatsreferenz</li>
              <li>Unterschreiben Sie das Mandat direkt im digitalen Signaturfeld</li>
              <li>Wir erzeugen automatisch ein PDF und speichern es für Sie ab</li>
            </ol>
          </div>

          {mandateReference && (
            <div className="mb-6 inline-block px-3 py-1 bg-primary/10 text-primary-strong rounded-md text-xs font-medium border border-primary/25">
              Referenz: {mandateReference}
            </div>
          )}

          {!formData.directDebitSignature && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className="space-y-2">
                <Label>Name des Zahlungsdienstleisters</Label>
                <Input 
                  value={formData.payerServiceProviderName}
                  onChange={handleSepaFieldChange('payerServiceProviderName')}
                  className="bg-card"
                />
              </div>
              <div className="space-y-2">
                <Label>Ort</Label>
                <Input 
                  value={formData.payerCity}
                  onChange={handleSepaFieldChange('payerCity')}
                  className="bg-card"
                />
              </div>
              <div className="space-y-2">
                <Label>IBAN</Label>
                <Input 
                  value={formData.payerIban}
                  onChange={handleSepaFieldChange('payerIban', v => v.replace(/\s+/g, '').toUpperCase())}
                  className="bg-card"
                  maxLength={34}
                />
              </div>
              <div className="space-y-2">
                <Label>BIC</Label>
                <Input 
                  value={formData.payerBic}
                  onChange={handleSepaFieldChange('payerBic', v => v.replace(/\s+/g, '').toUpperCase())}
                  className="bg-card"
                  maxLength={11}
                />
              </div>
            </div>
          )}

          {successMessage && (
            <Alert className="mb-6 bg-success/10 border-success/25 text-success-strong">
              <CheckCircle2 className="h-4 w-4 text-success-strong" />
              <AlertDescription>{successMessage}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            {!formData.directDebitSignature ? (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Edit className="h-4 w-4 text-[#1967D2]" />
                  Digitale Unterschrift
                </Label>
                <div
                  ref={directDebitCanvasContainerRef}
                  className="border rounded-xl bg-card overflow-hidden h-[200px] relative shadow-sm"
                >
                  <SignatureCanvas
                    ref={directDebitSignatureRef}
                    canvasProps={{ className: 'w-full h-full cursor-crosshair' }}
                    backgroundColor="rgba(0,0,0,0)"
                  />
                  <div className="absolute bottom-4 left-4 right-4 h-[1px] bg-[#1967D2]/20" />
                  <div className="absolute bottom-2 right-4 text-[10px] text-muted-foreground/50 pointer-events-none">Hier unterschreiben</div>
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" onClick={clearDirectDebitSignature} disabled={isDeletingMandate} className="flex-1">
                    {isDeletingMandate ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />} Löschen
                  </Button>
                  <Button onClick={saveDirectDebitSignature} disabled={isGeneratingMandate} className="flex-1 bg-[#1967D2] hover:bg-[#1756B6]">
                    {isGeneratingMandate ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                    Speichern
                  </Button>
                </div>
              </div>
            ) : (
              <div className="bg-success/10 border border-success/25 rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-success/10 flex items-center justify-center text-success-strong">
                    <Check className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-success-strong">Unterschrift gespeichert</p>
                    <p className="text-xs text-success-strong">Bereit für PDF-Erstellung</p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={clearDirectDebitSignature} disabled={isDeletingMandate} title="Lastschrift-Mandat löschen" className="text-muted-foreground hover:text-destructive-strong">
                  {isDeletingMandate ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                </Button>
              </div>
            )}

            <div className="flex gap-2 pt-4 border-t border-border">
               <Button variant="outline" size="sm" onClick={() => regenerateMandateReference(true)} className="flex-1 text-xs">
                 <RefreshCw className="h-3 w-3 mr-2" /> Neue Referenz
               </Button>
               <Button 
                 variant="outline" 
                 size="sm" 
                 onClick={handleDownloadMandate}
                 disabled={!formData.directDebitSignature && !mandateExists}
                 className="flex-1 text-xs"
               >
                 <Download className="h-3 w-3 mr-2" /> Download
               </Button>
            </div>
          </div>
        </Card>
          )}
        </>
      )}

      {/* Footer Navigation */}
      <div className="flex justify-between pt-6">
        <Button variant="outline" onClick={handlePrevStep} className="px-6 h-11 border-border">
          <ArrowLeft className="mr-2 h-4 w-4" /> Zurück
        </Button>
        <Button onClick={handleNextStep} className="bg-[#1967D2] hover:bg-[#1756B6] text-white px-8 h-11">
          {currentStep < 2 ? 'Weiter' : 'Abschließen'} <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default ContractStep;
